import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Parser from 'html-react-parser';
import {
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModal } from '../actions';
import { switchCollectionLike } from '../actions/Collections';
import { switchFileLike } from '../actions/Files';
import {
  getShare,
  switchShareCollectionLike,
  switchShareFileLike,
} from '../actions/Shares';

import { userCanSeeLikes } from '../helpers/Auth';

class GridItemLikes extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      savingAction: null,
      saving: false,
    };

    this.handleSwitchLike = this.handleSwitchLike.bind(this);
  }

  componentDidMount() {
    this.isComponentMounted = true;
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
  }

  getItem() {
    const {
      collection,
      file,
    } = this.props;
    return collection || file;
  }

  getMode() {
    const { collection } = this.props;
    return collection ? 'collection' : 'file';
  }

  getLikesCount() {
    const { savingAction } = this.state;

    let count = this.getItem().likes_count;
    if (savingAction === 'like') {
      count += 1;
    }
    if (savingAction === 'unlike') {
      count -= 1;
    }

    return Math.max(count, 0);
  }

  getLikesList() {
    const { intl } = this.props;
    const item = this.getItem();

    if (!item.likes) {
      return '';
    }

    if (item.likes_count === 0) {
      if (this.getMode() === 'collection') {
        return intl.formatMessage({ defaultMessage: 'No one favorited this collection yet' });
      }

      return intl.formatMessage({ defaultMessage: 'No one favorited this file yet' });
    }

    let text = '';
    text = item.likes.slice(0, 5).map(l => (l.name)).join('<br />');
    if (item.likes_count > 5) {
      const moreText = intl.formatMessage({ defaultMessage: 'more' });
      text = `${text}<br />+${item.likes_count - 5} ${moreText}...`;
    }

    return text;
  }

  likedClass() {
    const { savingAction } = this.state;

    let className = '';
    if (savingAction === 'like') {
      className = ' file-liked';
    } else if (savingAction === 'unlike') {
      className = '';
    } else if (this.getItem().liked) {
      className = ' file-liked';
    }

    return className;
  }

  updateState(response) {
    const { updateCallback } = this.props;

    updateCallback(response);
    if (this.isComponentMounted) {
      this.setState({
        saving: false,
        savingAction: null,
      });
    }
  }

  handleSwitchLike() {
    const {
      share,
      switchCollectionLikeAction,
      switchShareCollectionLikeAction,
      switchFileLikeAction,
      switchShareFileLikeAction,
      readOnly,
    } = this.props;
    const { saving } = this.state;

    if (saving || readOnly) {
      return;
    }

    const item = this.getItem();
    this.setState({ saving: true });
    if (item.liked) {
      this.setState({ savingAction: 'unlike' });
    } else {
      this.setState({ savingAction: 'like' });
    }

    if (this.getMode() === 'collection') {
      if (share) {
        switchShareCollectionLikeAction(share, item).then((response) => {
          this.updateState(response);
        });
      } else {
        switchCollectionLikeAction(item).then((response) => {
          this.updateState(response);
        });
      }
    } else if (share) {
      switchShareFileLikeAction(share, item).then((response) => {
        this.updateState(response);
      });
    } else {
      switchFileLikeAction(item).then((response) => {
        this.updateState(response);
      });
    }
  }

  render() {
    const {
      intl,
      inModal,
      likesCount,
      share,
      openLikesModal,
      readOnly,
    } = this.props;
    const item = this.getItem();
    const suffix = inModal ? '-m' : item.uuid;

    if (item.liked === null) {
      return null;
    }

    if (
      (!share && !userCanSeeLikes()) ||
      (share && !share.allow_favoriting)
    ) {
      return null;
    }

    return (
      <span className={classnames('grid-item-like', 'file-like', { 'share-like': share })}>
        {/* eslint-disable jsx-a11y/click-events-have-key-events */}
        <span
          id={`LikeBtn-${suffix}`}
          className={classnames(
            `file-like-btn${this.likedClass()}`,
            { 'file-like-btn-disabled': readOnly },
          )}
          role="button"
          tabIndex={item.uuid}
          onClick={this.handleSwitchLike}
        >
          <i className="fas fa-star" />
        </span>
        {/* eslint-enable jsx-a11y/click-events-have-key-events */}
        {!readOnly && (
          <UncontrolledTooltip fade={false} placement="bottom" target={`LikeBtn-${suffix}`}>
            {
            item.liked ?
              intl.formatMessage({ defaultMessage: 'Remove from favorites' }) :
              intl.formatMessage({ defaultMessage: 'Add to favorites' })
          }
          </UncontrolledTooltip>
        )}
        {likesCount && (
          <>
            <Button
              id={`LikesNo-${suffix}`}
              className={classnames('file-likes-no', { 'file-likes-no-disabled': !!share })}
              onClick={() => {
                if (readOnly) {
                  return;
                }
                if (item.likes_count > 0 && !share) {
                  openLikesModal(this.getMode(), share?.uuid, item);
                }
              }}
              disabled={readOnly}
            >
              {this.getLikesCount()}
            </Button>
            {!share && (
              <UncontrolledTooltip fade={false} placement="bottom" target={`LikesNo-${suffix}`} className="likes-tooltip">
                {Parser(this.getLikesList())}
              </UncontrolledTooltip>
            )}
          </>
        )}
      </span>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  switchFileLikeAction: file => (
    dispatch(switchFileLike(file))
  ),
  switchShareFileLikeAction: (share, file) => (
    dispatch(switchShareFileLike(share.uuid, file)).then(() => (
      dispatch(getShare({ ...share, updated_at: new Date() }))
    ))
  ),
  switchCollectionLikeAction: collection => (
    dispatch(switchCollectionLike(collection))
  ),
  switchShareCollectionLikeAction: (share, collection) => (
    dispatch(switchShareCollectionLike(share.uuid, collection)).then(() => (
      dispatch(getShare({ ...share, updated_at: new Date() }))
    ))
  ),
  openLikesModal: (mode, shareUuid, item) => {
    dispatch(showModal('LikesModal', {
      className: 'modal-likes modal-likes-file',
      backdrop: true,
      collection: mode === 'collection' ? item : undefined,
      file: mode === 'file' ? item : undefined,
      shareUuid,
    }));
  },
});

GridItemLikes.propTypes = {
  intl: PropTypes.object.isRequired,
  file: PropTypes.object,
  collection: PropTypes.object,
  switchCollectionLikeAction: PropTypes.func.isRequired,
  switchShareCollectionLikeAction: PropTypes.func.isRequired,
  switchFileLikeAction: PropTypes.func.isRequired,
  switchShareFileLikeAction: PropTypes.func.isRequired,
  openLikesModal: PropTypes.func.isRequired,
  share: PropTypes.object,
  inModal: PropTypes.bool,
  updateCallback: PropTypes.func,
  likesCount: PropTypes.bool,
  readOnly: PropTypes.bool,
};

GridItemLikes.defaultProps = {
  file: null,
  collection: null,
  share: null,
  inModal: false,
  likesCount: true,
  readOnly: false,
  updateCallback: () => {},
};

export default connect(null, mapDispatchToProps)(injectIntl(GridItemLikes));
